import React, { useState } from "react";
import { grantFeatureAccess, revokeFeatureAccess } from "../../api/paidClientsService";
import { channelLabel, FEATURES, PERIODS } from "../../utils/constants";
import { fmtDate, fmtDayMonth, fmtMoney } from "../../utils/format";

function plusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
}

function periodLabel(s) {
  if (s.isAnnual) return "годовая";
  return PERIODS[s.period] || null;
}

const SubscriptionTab = ({ userId, detail, notify, reloadDetail, onChanged }) => {
  const [granting, setGranting] = useState(false);
  const [feature, setFeature] = useState(Object.keys(FEATURES)[0]);
  const [endAt, setEndAt] = useState(plusDays(30));
  const [busy, setBusy] = useState(false);

  const sub = detail.subscription || {};
  const cur = sub.current || null;
  const history = sub.history || [];
  const access = sub.featureAccess || [];

  const afterChange = () => {
    reloadDetail();
    onChanged();
  };

  const grant = async () => {
    setBusy(true);
    try {
      // окончание дня по Астане
      await grantFeatureAccess(userId, feature, endAt ? `${endAt}T23:59:59+05:00` : undefined);
      notify("Доступ выдан");
      setGranting(false);
      afterChange();
    } catch (e) {
      notify(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (f) => {
    if (!window.confirm(`Закрыть доступ «${FEATURES[f] || f}»?`)) return;
    try {
      await revokeFeatureAccess(userId, f);
      notify("Доступ закрыт");
      afterChange();
    } catch (e) {
      notify(e.message);
    }
  };

  return (
    <>
      <div className="pc-card">
        <h3>Текущая подписка</h3>
        {cur ? (
          <div className="pc-kv">
            <div className="k">Тариф</div>
            <span>
              <b>{cur.tariff || "—"}</b>
              {periodLabel(cur) && ` (${periodLabel(cur)})`}
              {sub.isActive === false && <span className="tag acc xs">не активна</span>}
            </span>
            <div className="k">Период</div>
            <span>
              с {fmtDate(cur.startAt)} по {fmtDate(cur.endAt)}
            </span>
            <div className="k">Осталось</div>
            <span>
              {cur.daysLeft === null || cur.daysLeft === undefined ? (
                "—"
              ) : cur.daysLeft < 0 ? (
                <span className="tag acc">истекла {fmtDate(cur.endAt)}</span>
              ) : cur.daysLeft === 0 ? (
                <span className="days red">сегодня</span>
              ) : (
                <span className={`days ${cur.daysLeft <= 7 ? "red" : ""}`}>{cur.daysLeft} дн.</span>
              )}
            </span>
            <div className="k">Способ получения</div>
            <span>{channelLabel(cur)}</span>
            {cur.amount ? (
              <>
                <div className="k">Сумма</div>
                <span>{fmtMoney(cur.amount)}</span>
              </>
            ) : null}
          </div>
        ) : (
          <div className="muted">Нет данных о подписке</div>
        )}
      </div>

      <div className="pc-card">
        <h3>История подписок и оплат</h3>
        {history.length ? (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Тариф</th>
                <th>Период</th>
                <th>Канал / Кто выдал</th>
                <th className="r">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {history.map((s, i) => (
                <tr key={s.paymentId || i}>
                  <td>
                    {s.tariff}
                    {periodLabel(s) && <span className="muted small"> · {periodLabel(s)}</span>}
                  </td>
                  <td>
                    {fmtDate(s.startAt)} — {fmtDate(s.endAt)}
                  </td>
                  <td>{channelLabel(s)}</td>
                  <td className="r">{s.amount ? fmtMoney(s.amount) : <span className="zero">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="muted">Записей нет</div>
        )}
        <div className="pc-kv" style={{ marginTop: 12 }}>
          <div className="k">Продлевал</div>
          <b>{sub.renewals ?? 0} раз</b>
          <div className="k">Всего оплатил (LTV)</div>
          <b>{fmtMoney(sub.totalPaid ?? 0)}</b>
        </div>
      </div>

      <div className="pc-card">
        <h3>Доступы к функциям (бета PRO+)</h3>
        {access.length ? (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Функция</th>
                <th>Срок</th>
                <th>Согласие клиента</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {access.map((a, i) => (
                <tr key={`${a.feature}-${a.startAt || i}`} className={a.isActive ? "" : "mine"}>
                  <td>{FEATURES[a.feature] || a.feature}</td>
                  <td>
                    {a.isActive ? "до " : "был до "}
                    {fmtDate(a.endAt)}
                  </td>
                  <td>
                    {a.consentAt ? (
                      <span className="tag ok">дано {fmtDayMonth(a.consentAt)}</span>
                    ) : (
                      <span className="tag">не дано</span>
                    )}
                  </td>
                  <td className="r">
                    {a.isActive && (
                      <button type="button" className="pc-btn sm danger" onClick={() => revoke(a.feature)}>
                        Закрыть доступ
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="muted">Доступы не выдавались</div>
        )}

        {granting ? (
          <div className="pc-row" style={{ marginTop: 10 }}>
            <select className="pc-select" value={feature} onChange={(e) => setFeature(e.target.value)}>
              {Object.entries(FEATURES).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
            <span className="muted">до</span>
            <input
              type="date"
              className="pc-input browser-default"
              value={endAt}
              min={plusDays(0)}
              onChange={(e) => setEndAt(e.target.value)}
            />
            <button type="button" className="pc-btn sm primary" onClick={grant} disabled={busy}>
              Выдать
            </button>
            <button type="button" className="pc-btn sm" onClick={() => setGranting(false)}>
              Отмена
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="pc-btn sm"
            style={{ marginTop: 10 }}
            onClick={() => setGranting(true)}
          >
            + Выдать доступ к функции
          </button>
        )}
      </div>
    </>
  );
};

export default SubscriptionTab;
